"use client";
import {
  Box,
  Button,
  ButtonProps,
  Flex,
  Heading,
  ThemeTypings,
  useBreakpointValue,
} from "@chakra-ui/react";
import { PageHero } from "lib/gcms";
import Image from "next/image";
import React, { MutableRefObject } from "react";
import { fmtThemeColor } from "utils";

interface HeroProps extends Omit<PageHero, "subtitle"> {
  title?: string;
  subtitle?: React.ReactNode;
  scrollRef?: MutableRefObject<null>;
}
export function PodcastHero({
  title,
  subtitle,
  image,
  buttons,
  bg,
  color,
}: HeroProps) {
  const buttonSize: ButtonProps["size"] = useBreakpointValue(
    {
      base: "md",
      lg: "lg",
    },
    { fallback: "base" },
  );

  const handleClick = () => {
    const element = document.getElementById("podcast-player");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <Box
      as="section"
      py={{ base: "4", lg: "20" }}
      overflow="hidden"
      bgGradient={`linear(to-r, ${fmtThemeColor(bg).split(".")[0]}.100, ${
        fmtThemeColor(bg).split(".")[0]
      }.50)`}
      color={fmtThemeColor(color)}
    >
      <Box
        maxW={{ base: "xl", md: "7xl" }}
        mx="auto"
        px={{ base: "6", md: "8" }}
      >
        <Flex
          align="center"
          direction={{ base: "column-reverse", lg: "row" }}
          justify={{
            base: "center",
            lg: "space-between",
          }}
        >
          <Box
            flex="1"
            px={{
              lg: "6",
            }}
            py={{
              base: "6",
              lg: "24",
            }}
            textAlign={{
              base: "center",
              lg: "left",
            }}
          >
            <Heading
              as="h1"
              fontWeight="extrabold"
              display={{
                base: "contents",
              }}
              fontSize={{
                base: "3xl",
                md: "4xl",
                lg: "6xl",
              }}
            >
              {title}
            </Heading>

            {subtitle && (
              <Box
                as="div"
                mt={{ base: "2", lg: "4" }}
                mb={{ base: "2", lg: "4" }}
                display="flex"
                flexDir="column"
                sx={{
                  "h2 span": {
                    fontSize: {
                      base: "lg",
                      md: "2xl",
                    },
                    fontWeight: "light",
                  },
                }}
              >
                {subtitle}
              </Box>
            )}
            {buttons?.map((button, idx) => (
              <Button
                key={idx}
                colorScheme={button.theme as ThemeTypings["colorSchemes"]}
                size={buttonSize}
                fontSize={{
                  base: "auto",
                  lg: "xl",
                }}
                w={{
                  base: "auto",
                }}
                mr={{
                  base: "0",
                  md: "4",
                }}
                my="2"
                borderRadius={"full"}
                onClick={handleClick}
              >
                {button.label}
              </Button>
            ))}
          </Box>
          <Box
            pos="relative"
            marginEnd={{
              base: 0,
            }}
            w={{
              base: "50%",
              md: "30%",
            }}
          >
            <Image
              style={{ objectFit: "contain" }}
              src={image?.url || ""}
              alt={image?.alt ?? "Green podcast icon on a smartphone"}
              width={image?.width || 0}
              height={image?.height || 0}
              crossOrigin="anonymous"
              blurDataURL={image?.blurDataURL ?? undefined}
            />
          </Box>
        </Flex>
      </Box>
    </Box>
  );
}

export default PodcastHero;
